import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Clock, MapPin, Users, MessageCircle, ChevronDown, ChevronUp, Trophy, BarChart3, Youtube, FileText, Settings } from 'lucide-react';
import SubscribeButton from '../components/SubscribeButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = '+351961792888';

const MatchesPage = () => {
  const [matches, setMatches] = useState([]);
  const [expandedMatches, setExpandedMatches] = useState({});

  useEffect(() => {
    fetchMatches();
  }, []);

  const fetchMatches = async () => {
    try {
      const response = await axios.get(`${API}/matches`);
      setMatches(response.data);
    } catch (error) {
      console.error('Erro ao carregar partidas:', error);
    }
  };

  const getAvailableSpots = (match) => {
    return match.total_spots - match.confirmed_players.length;
  };

  const handleWhatsAppClick = (match) => {
    const message = `Olá! Vi que há ${getAvailableSpots(match)} vaga(s) disponível(is) para o jogo do dia ${match.date} às ${match.time}. Gostaria de participar!`;
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const toggleExpand = (matchId) => {
    setExpandedMatches(prev => ({
      ...prev,
      [matchId]: !prev[matchId]
    }));
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-PT', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-zinc-950 text-white py-4 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a href="/" className="text-2xl sm:text-3xl font-black tracking-tighter hover:text-green-400 transition-colors">
            ⚽ DOMINGÃO
          </a>
          <div className="flex items-center gap-2">
            <SubscribeButton />
            <a href="/admin" className="text-sm font-bold hover:text-green-400 transition-colors" data-testid="go-to-admin">
              <Settings size={24} />
            </a>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b-2 border-zinc-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            <a href="/" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-home">
              HOME
            </a>
            <a href="/votar" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-vote">
              🗳️ VOTAR
            </a>
            <a href="/estatisticas" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-stats">
              <BarChart3 className="inline mr-1" size={16} /> ESTATÍSTICAS
            </a>
            <a href="/torneios" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-torneios">
              <Trophy className="inline mr-1" size={16} /> TORNEIOS
            </a>
            <a href="/partidas" className="flex-shrink-0 bg-green-500 text-white px-4 py-2 rounded-lg font-bold text-sm" data-testid="nav-matches">
              <Calendar className="inline mr-1" size={16} /> PARTIDAS
            </a>
            <a href="/rankings" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-rankings">
              <Trophy className="inline mr-1" size={16} /> RANKINGS
            </a>
            <a href="/replays" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-replays">
              <Youtube className="inline mr-1" size={16} /> REPLAYS
            </a>
            <a href="/regras" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-rules">
              <FileText className="inline mr-1" size={16} /> REGRAS
            </a>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Page Title */}
        <div className="mb-6">
          <h1 className="text-2xl font-black tracking-tight uppercase flex items-center gap-2">
            <Calendar size={28} /> PRÓXIMAS PARTIDAS
          </h1>
          <p className="text-zinc-500 text-sm mt-1">Veja os jogos agendados e reserve sua vaga!</p>
        </div>

        {/* WhatsApp Group Invite */}
        <a
          href="https://chat.whatsapp.com/FclBi3J8x1XBFRL07HvftK"
          target="_blank"
          rel="noopener noreferrer"
          className="block mb-5 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white shadow-lg border-2 border-emerald-700 transition-all hover:scale-[1.01] active:scale-[0.99]"
          data-testid="whatsapp-group-invite"
        >
          <div className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center shrink-0">
              <MessageCircle size={26} className="text-white" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold uppercase tracking-widest opacity-90">📱 Grupo Oficial</p>
              <p className="text-lg font-black leading-tight">Entra no grupo do WhatsApp</p>
              <p className="text-xs opacity-90">Novidades das partidas, sorteio dos times e tudo do Domingão em primeira mão.</p>
            </div>
            <span className="hidden sm:inline-block bg-white text-green-700 font-black px-4 py-2 rounded-lg text-sm shrink-0">
              ENTRAR
            </span>
          </div>
        </a>

        {/* Matches List */}
        {matches.length === 0 ? (
          <div className="bg-white rounded-xl border-2 border-zinc-200 p-8 text-center">
            <p className="text-xl font-bold text-zinc-400 mb-2">Nenhuma partida agendada</p>
            <p className="text-zinc-500">Fique atento, novos jogos serão publicados em breve!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {matches.map((match) => {
              const availableSpots = getAvailableSpots(match);
              const isFull = availableSpots <= 0;
              const isExpanded = expandedMatches[match.id];
              
              return (
                <div 
                  key={match.id} 
                  className={`bg-white rounded-xl border-2 overflow-hidden transition-all ${
                    isFull ? 'border-red-300 bg-red-50/30' : 'border-zinc-200'
                  }`}
                  data-testid={`match-card-${match.id}`}
                >
                  {/* Match Header */}
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        {/* Date Badge */}
                        <div className="inline-flex items-center gap-2 bg-zinc-950 text-white px-3 py-1 rounded-full text-sm font-bold mb-3">
                          <Calendar size={14} />
                          {formatDate(match.date)}
                        </div>
                        
                        {/* Location */}
                        <h3 className="text-xl font-black mb-2 flex items-center gap-2">
                          <MapPin size={20} className="text-green-500" />
                          {match.location_url ? (
                            <a
                              href={match.location_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:text-green-600 hover:underline"
                              data-testid={`match-location-link-${match.id}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {match.location}
                            </a>
                          ) : (
                            match.location
                          )}
                        </h3>
                        
                        {/* Time and Spots */}
                        <div className="flex flex-wrap gap-3 text-sm">
                          <span className="flex items-center gap-1 text-zinc-600">
                            <Clock size={16} /> {match.time}
                          </span>
                          <span className={`flex items-center gap-1 font-bold ${
                            isFull ? 'text-red-600' : availableSpots <= 3 ? 'text-yellow-600' : 'text-green-600'
                          }`}>
                            <Users size={16} />
                            {isFull ? 'LOTADO' : `${availableSpots} vagas livres`}
                          </span>
                        </div>
                      </div>
                      
                      {/* WhatsApp Button */}
                      {!isFull && (
                        <button
                          onClick={() => handleWhatsAppClick(match)}
                          className="bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-xl font-bold text-sm flex items-center gap-2 transition-colors"
                          data-testid={`whatsapp-btn-${match.id}`}
                        >
                          <MessageCircle size={20} />
                          <span className="hidden sm:inline">QUERO JOGAR</span>
                        </button>
                      )}
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mt-4">
                      <div className="flex justify-between text-xs font-bold mb-1">
                        <span>{match.confirmed_players.length} confirmados</span>
                        <span>{match.total_spots} vagas</span>
                      </div>
                      <div className="h-2 bg-zinc-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all ${isFull ? 'bg-red-500' : 'bg-green-500'}`}
                          style={{ width: `${(match.confirmed_players.length / match.total_spots) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Confirmed Players Section */}
                  {match.confirmed_players.length > 0 && (
                    <div className="border-t border-zinc-200">
                      <button
                        onClick={() => toggleExpand(match.id)}
                        className="w-full px-4 py-3 flex items-center justify-between text-sm font-bold text-zinc-600 hover:bg-zinc-50 transition-colors"
                        data-testid={`toggle-players-${match.id}`}
                      >
                        <span>👥 Ver jogadores confirmados ({match.confirmed_players.length})</span>
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </button>
                      
                      {isExpanded && (
                        <div className="px-4 pb-4">
                          <div className="flex flex-wrap gap-2">
                            {match.confirmed_players.map((player, idx) => (
                              <span 
                                key={idx}
                                className="bg-zinc-100 px-3 py-1 rounded-full text-sm font-bold"
                              >
                                {player}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Info Box */}
        <div className="mt-8 bg-green-50 border-2 border-green-500 rounded-xl p-4">
          <h3 className="font-bold text-green-700 mb-2">📱 Como reservar vaga?</h3>
          <p className="text-sm text-green-800">
            Clique no botão "QUERO JOGAR" para enviar uma mensagem diretamente no WhatsApp do organizador.
            As vagas são confirmadas por ordem de chegada!
          </p>
        </div>
      </main>
    </div>
  );
};

export default MatchesPage;
